import { useTranslation } from 'react-i18next';

import DateRangePicker, {
    type DateRange,
} from '@/components/molecules/DateRangePicker/DateRangePicker';
import type { RetryProps } from '@/components/retryFailedQueries';
import type { DomainFeedConversionRateDay } from '@/domain/biomass';
import type { DomainBiomassDay, DomainBioscopeDay } from '@/domain/cages/overview.types';
import { OverviewChartCard, useOverviewChartSpecs } from '@/routes/Client/Cage/overviewCharts';
import GridLayout from '@/routes/Client/Cage/pages/Reports/shared/GridLayout';
import { formatDateTimeWorded } from '@/utils/date';

import styles from './CageAllGraphsView.module.scss';

export interface CageAllGraphsViewProps {
    days: DomainBiomassDay[];
    /** Dayparts that get a series, from `filterGraphDayparts`. */
    graphedDaypartTags: string[];
    /** Dayparts that start visible. */
    activeDaypartTags: string[];
    /** The single daypart the charts that do not fan out should plot. */
    daypartTag: string;
    weightUnitTag: string;
    isLoading: boolean;
    error?: unknown;
    /** Try again in the charts' error state. */
    retry?: RetryProps;
    dateFrom?: string;
    dateTo?: string;
    onDateChange: (dates: DateRange) => void;
    /** From a separate endpoint, so it carries its own loading and error state. */
    feedConversionRateDays: DomainFeedConversionRateDay[];
    isFeedConversionRateLoading: boolean;
    feedConversionRateError?: unknown;
    feedConversionRateRetry?: RetryProps;
    /** True while a live feeding refresh is running for this cage. */
    isFeedConversionRateRefreshing: boolean;
    /** True once the live-refresh poll budget is spent while still refreshing. */
    isFeedConversionRateExhausted: boolean;
    /** Newest fetch time for this cage's feeding rows; null until one has landed. */
    feedConversionRateLastFetchedAt: Date | null;
    /**
     * True when the other charts answer for a fish group (`fg`). The feeding service only knows
     * cages, so the FCR still covers the whole cage and its card says so.
     */
    isFeedConversionRateCageLevel?: boolean;
    /** Camera telemetry: depth, temperature and the admin diagnostics. */
    bioscopeDays: DomainBioscopeDay[];
    /** Gates the bioscope diagnostic charts. */
    isOptoScaleAdmin: boolean;
}

export default function CageAllGraphsView(props: CageAllGraphsViewProps) {
    const {
        days,
        graphedDaypartTags,
        activeDaypartTags,
        daypartTag,
        weightUnitTag,
        isLoading,
        error,
        retry,
        dateFrom,
        dateTo,
        onDateChange,
        feedConversionRateDays,
        isFeedConversionRateLoading,
        feedConversionRateError,
        feedConversionRateRetry,
        isFeedConversionRateRefreshing,
        isFeedConversionRateExhausted,
        feedConversionRateLastFetchedAt,
        isFeedConversionRateCageLevel = false,
        bioscopeDays,
        isOptoScaleAdmin,
    } = props;
    const { t } = useTranslation('cageCharts');
    // Page chrome, not chart copy: stays on the shared root keys.
    const { t: tRoot } = useTranslation();

    const specs = useOverviewChartSpecs({
        days,
        graphedDaypartTags,
        activeDaypartTags,
        daypartTag,
        weightUnitTag,
        feedConversionRateDays,
        bioscopeDays,
    });

    /**
     * Every chart on this page reads the same date range, so the picker lives once
     * at page level and the cards render only their own actions menu.
     */
    const shared = {
        isLoading,
        error,
        ...retry,
        showDateRangePicker: false,
        contentBlockSpacing: 'none' as const,
    };

    return (
        <>
            <div className={styles.datePickerSlot}>
                <div className={styles.datePickerRow}>
                    <DateRangePicker
                        dateFrom={dateFrom}
                        dateTo={dateTo}
                        onDateChange={onDateChange}
                        defaultPresetId="none"
                        menuPosition="left"
                        disabled={isLoading}
                    />
                    {isFeedConversionRateRefreshing ? (
                        <p className={styles.refreshingHint}>
                            {isFeedConversionRateExhausted
                                ? feedConversionRateLastFetchedAt
                                    ? t('refresh.hintExhausted', {
                                          time: formatDateTimeWorded(
                                              feedConversionRateLastFetchedAt
                                          ),
                                      })
                                    : t('refresh.hintExhaustedNoTime')
                                : t('refresh.hint')}
                        </p>
                    ) : null}
                </div>
            </div>
            <GridLayout contentLayout="3">
                <OverviewChartCard
                    title={t('title.livingWeight')}
                    chartName="cage-living-weight"
                    spec={specs.livingWeight}
                    {...shared}
                />
                <OverviewChartCard
                    title={t('title.measurements')}
                    chartName="cage-measurements"
                    spec={specs.measurements}
                    {...shared}
                />
                <OverviewChartCard
                    title={t('title.growth')}
                    chartName="cage-growth"
                    spec={specs.growth}
                    {...shared}
                />
                <OverviewChartCard
                    title={t('title.depthTemperature')}
                    chartName="cage-depth-temperature"
                    spec={specs.depthTemperature}
                    {...shared}
                />
                <OverviewChartCard
                    title={t('title.size')}
                    chartName="cage-size"
                    spec={specs.size}
                    {...shared}
                />
                <OverviewChartCard
                    title={t('title.coefficientOfVariation')}
                    chartName="cage-cv"
                    spec={specs.coefficientOfVariation}
                    {...shared}
                />
                <OverviewChartCard
                    title={t('title.conditionFactor')}
                    chartName="cage-condition-factor"
                    spec={specs.conditionFactor}
                    {...shared}
                />
                <OverviewChartCard
                    title={t('title.feedConversionRatio')}
                    subtitle={
                        isFeedConversionRateCageLevel
                            ? t('subtitle.feedConversionRatioCageLevel')
                            : undefined
                    }
                    chartName="cage-feed-conversion-ratio"
                    spec={specs.feedConversionRatio}
                    showDateRangePicker={false}
                    contentBlockSpacing="none"
                    isLoading={isFeedConversionRateLoading}
                    error={feedConversionRateError}
                    {...feedConversionRateRetry}
                />
            </GridLayout>

            {isOptoScaleAdmin && (
                <>
                    <h2 className={styles.adminHeading}>
                        {tRoot('cageDashboard.cageAllGraphs.onlyVisibleForInternalOptoScale')}
                    </h2>
                    <GridLayout contentLayout="3">
                        <OverviewChartCard
                            title={t('title.pitchRoll')}
                            chartName="cage-pitch-roll"
                            spec={specs.pitchRoll}
                            {...shared}
                        />
                        <OverviewChartCard
                            title={t('title.sceneLabels')}
                            chartName="cage-scene-labels"
                            spec={specs.sceneLabels}
                            {...shared}
                        />
                        <OverviewChartCard
                            title={t('title.verticalDisparity')}
                            chartName="cage-vertical-disparity"
                            spec={specs.verticalDisparity}
                            {...shared}
                        />
                    </GridLayout>
                </>
            )}
        </>
    );
}
